import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoices as invoicesApi, clients as clientsApi, businesses } from '../api';
import { todayISO, addDays, formatCurrency, getCurrencySymbol } from '../utils/format';
import { getCountryConfig } from '../utils/countries';
import UpgradeModal from '../components/UpgradeModal';
import { useSubscription } from '../context/SubscriptionContext';

const emptyItem = () => ({ description: '', quantity: 1, rate: 0 });

export default function CreateInvoice() {
  const navigate = useNavigate();
  const { id } = useParams(); // if present, edit mode
  const isEdit = !!id;

  const [business, setBusiness] = useState(null);
  const [clientList, setClientList] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    invoice_date: todayISO(),
    due_date: addDays(todayISO(), 30),
    gst_percentage: 18,
    notes: '',
    status: 'draft',
  });
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const { canCreateInvoice, refresh: refreshSubscription } = useSubscription();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => {
    businesses.get().then(r => {
      setBusiness(r.data);
      setForm(f => ({ ...f, due_date: addDays(todayISO(), r.data.payment_terms_days || 30) }));
    });
    clientsApi.list().then(r => setClientList(r.data));

    if (isEdit) {
      invoicesApi.get(id).then(r => {
        const inv = r.data;
        setForm({
          client_id: inv.client_id,
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          gst_percentage: inv.gst_percentage,
          notes: inv.notes || '',
          status: inv.status,
        });
        setItems(inv.line_items.map(li => ({
          description: li.description,
          quantity: li.quantity,
          rate: li.rate,
        })));
      });
    }
  }, [id]);

  const countryConfig = getCountryConfig(business?.country || 'IN');

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.rate) || 0), 0);
  const gstAmount = (subtotal * (parseFloat(form.gst_percentage) || 0)) / 100;
  const total = subtotal + gstAmount;

  const validate = () => {
    const e = {};
    if (!form.client_id) e.client_id = 'Select a client';
    if (items.every(i => !i.description.trim())) e.items = 'Add at least one line item';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!canCreateInvoice) { setUpgradeModalOpen(true); return; }
    setSaving(true);
    const payload = {
      ...form,
      gst_percentage: parseFloat(form.gst_percentage),
      line_items: items
        .filter(i => i.description.trim())
        .map(i => ({ description: i.description, quantity: parseFloat(i.quantity) || 1, rate: parseFloat(i.rate) || 0 })),
    };
    try {
      if (isEdit) {
        await invoicesApi.update(id, payload);
        navigate(`/invoices/${id}`);
      } else {
        const res = await invoicesApi.create(payload);
        refreshSubscription();
        navigate(`/invoices/${res.data.id}`);
      }
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        setUpgradeModalOpen(true);
        return;
      }
    } finally { setSaving(false); }
  };

  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Invoice' : 'New Invoice'}</h1>
      </div>

      <form onSubmit={submit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Details */}
            <div className="card space-y-4">
              <h2 className="text-base font-semibold text-gray-900">Invoice Details</h2>

              <div>
                <label className="label">Client <span className="text-red-500">*</span></label>
                <select
                  className={`input ${errors.client_id ? 'border-red-400' : ''}`}
                  value={form.client_id}
                  onChange={e => setForm({ ...form, client_id: e.target.value })}
                >
                  <option value="">Select client...</option>
                  {clientList.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>
                  ))}
                </select>
                {errors.client_id && <p className="text-red-500 text-xs mt-1">{errors.client_id}</p>}
                {clientList.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    No clients yet. <a href="/clients" className="text-primary-600 hover:underline">Add a client first</a>
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Invoice Date</label>
                  <input type="date" className="input" value={form.invoice_date}
                    onChange={e => setForm({ ...form, invoice_date: e.target.value })} />
                </div>
                <div>
                  <label className="label">Due Date</label>
                  <input type="date" className="input" value={form.due_date}
                    onChange={e => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>

              <div>
                <label className="label">Status</label>
                <select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Line Items */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">Line Items</h2>
                {errors.items && <p className="text-red-500 text-xs">{errors.items}</p>}
              </div>

              <div className="space-y-3 mb-4">
                <div className="hidden md:grid md:grid-cols-12 gap-2 text-xs text-gray-500 font-medium px-1">
                  <span className="col-span-1" />
                  <span className="col-span-5">Description</span>
                  <span className="col-span-2 text-right">Qty</span>
                  <span className="col-span-2 text-right">Rate ({getCurrencySymbol()})</span>
                  <span className="col-span-2 text-right">Amount</span>
                </div>
                {items.map((item, idx) => (
                  <div key={idx}>
                    {/* Mobile layout */}
                    <div className="md:hidden border border-gray-100 rounded-lg p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <input
                          className="input flex-1"
                          placeholder="Item description"
                          value={item.description}
                          onChange={e => updateItem(idx, 'description', e.target.value)}
                        />
                        {items.length > 1 && (
                          <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 p-2 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-1 items-center">
                        <div className="min-w-0">
                          <label className="text-[11px] text-gray-400">Qty</label>
                          <input
                            className="input text-right text-sm"
                            type="number" min="0" step="0.01"
                            value={item.quantity}
                            onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className="text-[11px] text-gray-400">Rate</label>
                          <input
                            className="input text-right text-sm"
                            type="number" min="0" step="0.01"
                            value={item.rate}
                            onChange={e => updateItem(idx, 'rate', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-400">Amount</label>
                          <div className="text-sm font-medium text-gray-700 truncate text-right">
                            {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop layout */}
                    <div className="hidden md:grid md:grid-cols-12 gap-2 items-center">
                      <div className="col-span-1 flex justify-center">
                        {items.length > 1 && (
                          <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="col-span-5">
                        <input
                          className="input"
                          placeholder="Item description"
                          value={item.description}
                          onChange={e => updateItem(idx, 'description', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          className="input text-right"
                          type="number" min="0" step="0.01"
                          value={item.quantity}
                          onChange={e => updateItem(idx, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          className="input text-right"
                          type="number" min="0" step="0.01"
                          value={item.rate}
                          onChange={e => updateItem(idx, 'rate', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2 text-right text-sm font-medium text-gray-700">
                        {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={addItem} className="btn-secondary text-xs flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Item
              </button>
            </div>

            {/* Notes */}
            <div className="card">
              <label className="label">Notes / Terms</label>
              <textarea
                className="input min-h-[80px] resize-none"
                placeholder="Payment terms, bank details..."
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          {/* Sidebar Summary */}
          <div className="space-y-4">
            {/* Tax Settings */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                {countryConfig.showGstSlab ? 'GST Settings' : 'Tax Settings'}
              </h2>
              {countryConfig.showGstSlab ? (
                <div>
                  <label className="label">GST Rate (%)</label>
                  <select
                    className="input"
                    value={form.gst_percentage}
                    onChange={e => setForm({ ...form, gst_percentage: e.target.value })}
                  >
                    <option value="0">0% (Exempt)</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
              ) : (
                <div>
                  <label className="label">Tax (%)</label>
                  <input
                    className="input"
                    type="number" min="0" max="100" step="0.01"
                    value={form.gst_percentage}
                    onChange={e => setForm({ ...form, gst_percentage: e.target.value })}
                    placeholder="Enter tax percentage"
                  />
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="card">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{countryConfig.totalsTaxLabel} ({form.gst_percentage}%)</span>
                  <span>{formatCurrency(gstAmount)}</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-100 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <button type="submit" className="btn-primary w-full" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Invoice'}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary w-full">
              Cancel
            </button>
          </div>
        </div>
      </form>
      <UpgradeModal isOpen={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} />
    </div>
  );
}
